import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyMessages, getUnreadMessagesCount, setMessagesAsRead } from '../api/messagesApi';
import { useEffect } from 'react';

const useMessage = ({ fetchOnPageLoad = false, fetchUnreadMessagesCount = false }) => {
    const queryClient = useQueryClient();

    useEffect(() => {
        return () => queryClient.invalidateQueries({ queryKey: ['getMyMessages'] });
    }, []);

    const { mutateAsync: setMessagesAsReadMutateAsync, isLoading: setMessagesAsReadIsLoading } =
        useMutation({
            mutationFn: setMessagesAsRead,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['getUnreadMessagesCount'] });
            }
        });

    const { data: unreadMessagesCount, isLoading: unreadMessagesCountIsLoading } = useQuery({
        queryKey: ['getUnreadMessagesCount'],
        queryFn: getUnreadMessagesCount,
        staleTime: 30 * 60 * 1000,
        enabled: fetchUnreadMessagesCount
    });

    const {
        data: myMessagesData,
        isFetching: myMessagesDataIsFetching,
        isFetchingNextPage: myMessagesDataIsFetchingNextPage,
        fetchNextPage: myMessagesDataFetchNextPage
    } = useInfiniteQuery({
        queryKey: ['getMyMessages'],
        queryFn: (input) => {
            return getMyMessages({ next: input?.pageParam });
        },
        getNextPageParam: (lastPage) => {
            return lastPage?.links?.next || undefined;
        },
        staleTime: 30 * 60 * 1000,
        enabled: fetchOnPageLoad
    });

    useEffect(() => {
        if (!myMessagesData || myMessagesDataIsFetching) return;
        const recentReadMessagesIds = myMessagesData.pages
            ?.at(-1)
            ?.results?.filter((message) => !message.is_read)
            .map((messageData) => messageData.id);
        if (recentReadMessagesIds?.length > 0) {
            setMessagesAsReadMutateAsync({ readMessagesIds: recentReadMessagesIds });
        }
    }, []);

    return {
        myMessagesData,
        myMessagesDataIsFetching,
        myMessagesDataIsFetchingNextPage,
        myMessagesDataFetchNextPage,
        setMessagesAsReadMutateAsync,
        setMessagesAsReadIsLoading,
        unreadMessagesCount,
        unreadMessagesCountIsLoading
    };
};

export default useMessage;
